import React from "react";
import { Box, CircularProgress, Backdrop, Typography } from "@mui/material";
import styled, { keyframes } from "styled-components";

// SpinKit Animations
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const doubleBounce = keyframes`
  0%, 100% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
`;

const wave = keyframes`
  0%, 60%, 100% {
    transform: initial;
  }
  30% {
    transform: translateY(-15px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(99, 102, 241, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
`;

const chasingDots = keyframes`
  0%, 100% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
`;

const threeBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`;

const circle = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const textPulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

// SpinKit Components
const SpinContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

// Spinner 1: Rotating Circles
const Spinner1 = styled(Box)`
  display: inline-block;
  position: relative;
  width: 40px;
  height: 40px;
  
  & div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 32px;
    height: 32px;
    margin: 4px;
    border: 3px solid transparent;
    border-radius: 50%;
    animation: ${spin} 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #fff transparent transparent transparent;
    
    &:nth-child(1) {
      animation-delay: -0.45s;
    }
    
    &:nth-child(2) {
      animation-delay: -0.3s;
    }
    
    &:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
`;

// Spinner 2: Wave
const Spinner2 = styled(Box)`
  display: flex;
  gap: 4px;
  
  & div {
    width: 8px;
    height: 40px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 4px;
    animation: ${wave} 1.2s ease-in-out infinite;
    
    &:nth-child(1) { animation-delay: -1.2s; }
    &:nth-child(2) { animation-delay: -1.1s; }
    &:nth-child(3) { animation-delay: -1s; }
    &:nth-child(4) { animation-delay: -0.9s; }
    &:nth-child(5) { animation-delay: -0.8s; }
  }
`;

// Spinner 3: Pulse
const Spinner3 = styled(Box)`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

// Spinner 4: Three Bounce
const Spinner4 = styled(Box)`
  display: flex;
  gap: 8px;
  
  & div {
    width: 12px;
    height: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 100%;
    display: inline-block;
    animation: ${threeBounce} 1.4s ease-in-out infinite both;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
`;

// Button Loading Spinner
const ButtonSpinner = styled(Box)`
  display: inline-block;
  width: 16px;
  height: 16px;
  
  &:after {
    content: " ";
    display: block;
    width: 12px;
    height: 12px;
    margin: 2px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    border-color: #ffffff transparent #ffffff transparent;
    animation: ${spin} 1.2s linear infinite;
  }
`;

// Main Loading Container
const LoadingBackdrop = styled(Backdrop)`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%);
  backdrop-filter: blur(20px);
  z-index: 9999;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const LoadingText = styled(Typography)`
  color: white;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  animation: ${textPulse} 2s ease-in-out infinite;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const LoadingSubtext = styled(Typography)`
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  max-width: 300px;
  line-height: 1.4;
`;

const LoadingDots = styled.span`
  display: inline-block;
  
  &::after {
    content: '...';
    animation: ${textPulse} 1.5s ease-in-out infinite;
  }
`;

// Button Loading Component
const ButtonLoadingContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const LoadingTextSmall = styled.span`
  color: white;
  font-weight: 600;
  font-size: 16px;
`;

// Main Loading Component
export default function Loading({ 
  open = false, 
  message = "جاري التحميل", 
  subtext,
  isButtonLoading = false,
  buttonText = "معالجة...",
  spinnerType = "1" // 1, 2, 3, 4
}) {
  const renderSpinner = () => {
    switch(spinnerType) {
      case "2":
        return (
          <Spinner2>
            <div />
            <div />
            <div />
            <div />
            <div />
          </Spinner2>
        );
      case "3":
        return <Spinner3 />;
      case "4":
        return (
          <Spinner4>
            <div />
            <div />
            <div />
          </Spinner4>
        );
      default:
        return (
          <Spinner1>
            <div />
            <div />
            <div />
            <div />
          </Spinner1>
        );
    }
  };

  if (isButtonLoading) {
    return (
      <ButtonLoadingContainer>
        <ButtonSpinner />
        <LoadingTextSmall>{buttonText}</LoadingTextSmall>
      </ButtonLoadingContainer>
    );
  }

  return (
    <LoadingBackdrop open={open}>
      <LoadingContainer>
        {renderSpinner()}
        <LoadingText variant="h6">
          {message}
          <LoadingDots />
        </LoadingText>
        {subtext && (
          <LoadingSubtext variant="body2">
            {subtext}
          </LoadingSubtext>
        )}
      </LoadingContainer>
    </LoadingBackdrop>
  );
}

// Export individual components for specific use cases
export { 
  ButtonLoadingContainer, 
  ButtonSpinner, 
  LoadingTextSmall,
  Spinner1,
  Spinner2,
  Spinner3,
  Spinner4
};
